// services/riskService.js
//
// Kill switch + risk limits (crypto-backend-api Wave 3-C, feature 1).
//
// Backend contract (routes/risk.js, controllers/risk.js, services/riskControls.js).
// Every route is passportJWT + scope `trade`; the two `/global` ones additionally
// need scope `admin` AND Users.role === 'admin'.
//
//   GET  /api/risk            -> { limits, effective, halted, snapshot }
//   PUT  /api/risk/limits     { dailyMaxLoss?, maxOpenPositions?, maxNotional?,
//                               maxMarketBeta?, maxFactorBeta?, maxSectorWeight?,
//                               blockExitsOnHalt? }
//                             -> { limits, effective, mirrored, warning? }
//   POST /api/risk/halt       { reason, blockExits? } -> { halted, scope, by, at, ... }
//   POST /api/risk/resume     -> { resumed, wasHalted, globalHaltStillActive, warning? }
//   POST /api/risk/flatten    { keepTrading?, reason? } -> 202 { accepted, halted, ... }
//   POST /api/risk/halt/global   admin
//   POST /api/risk/resume/global admin
//
// ─── The three things this layer exists to get right ───────────────────────
//
// 1. **`0` is not "no limit" to the API, and it is not a valid limit either.**
//    The trade engine reads a stored 0 as UNLIMITED (services/riskGuard.js
//    `numOrNull`), so accepting `maxOpenPositions: 0` would turn "allow no
//    positions" into "allow any number of positions" — the exact inversion of
//    what the user asked for. The API rejects 0 with a 400; `normalizeLimit()`
//    below rejects it client-side too, so the user gets the hint before a round
//    trip. **`null` is how you clear a limit.**
//
// 2. **An omitted key means "leave it alone"; `null` means "clear it".** So the
//    form must send an explicit `null` for a field the user emptied, and must
//    NOT send a key it is not editing. `buildLimitsBody()` is the one place
//    that distinction is encoded.
//
// 3. **`limits` and `effective` can disagree.** `limits` is the durable row;
//    `effective` is what the engine will actually enforce (the Redis mirror).
//    They differ only when a mirror write failed or Redis was flushed — and
//    when they do, `effective` is the truth and the user must be told, because
//    they otherwise believe a limit is live that is not. Same story for
//    `mirrored: false` on a PUT, which comes back as **200**, not an error.
//
// `snapshot.dayPnL` is EQUITY-based and includes unrealized P&L: an open
// position deep under water counts against `dailyMaxLoss` immediately. Label it
// that way in the UI or users will dispute it.
import http from './http';

/** The three numeric limit dimensions, in the order the form shows them. */
export const LIMIT_FIELDS = [
  {
    key: 'dailyMaxLoss',
    label: 'Daily max loss',
    unit: '$',
    help: 'Trading halts for the rest of the day once the day’s P&L drops below −this. Equity-based: unrealized losses count immediately.',
  },
  {
    key: 'maxOpenPositions',
    label: 'Max open positions',
    unit: '',
    integer: true,
    help: 'New entries are refused while this many bot positions are already open. Exits always work.',
  },
  {
    key: 'maxNotional',
    label: 'Max notional per order',
    unit: '$',
    help: 'An order whose value exceeds this is refused.',
  },
];

/**
 * The three EXPOSURE limits (plan phase 4 item 5). The api refreshes the
 * book's FF-6 betas and sector weights every 15 min (`riskExposure:<uid>`),
 * and the engine refuses an entry that pushes a limited number further past
 * its cap — only worsening orders, so a short that brings beta down is fine.
 * A missing or stale snapshot fails OPEN; the portfolio page's exposure panel
 * shows the same breaches, so a stale guard is visible rather than silent.
 */
export const EXPOSURE_FIELDS = [
  {
    key: 'maxMarketBeta',
    label: 'Max market beta',
    unit: 'β',
    help: 'Entries that would push the book’s |β| to the market past this are refused.',
  },
  {
    key: 'maxFactorBeta',
    label: 'Max style-factor beta',
    unit: 'β',
    help: 'Same cap for size, value, profitability, investment and momentum.',
  },
  {
    key: 'maxSectorWeight',
    label: 'Max sector weight',
    unit: 'fraction',
    max: 1,
    help: 'Gross weight of any one sector as a fraction of equity (0.35 = 35%).',
  },
];

/** Every numeric limit the PUT accepts, in form order. */
export const ALL_LIMIT_FIELDS = [...LIMIT_FIELDS, ...EXPOSURE_FIELDS];

/**
 * Client-side mirror of the API's limit rule.
 *
 * @param {string|number|null|undefined} raw
 * @returns {{ok:true, value:number|null} | {ok:false, error:string}}
 *   `value: null` means "clear this limit" (no limit).
 */
export const normalizeLimit = (raw, { integer = false, max = null, label = 'This limit' } = {}) => {
  if (raw === '' || raw === null || raw === undefined) return { ok: true, value: null };
  const n = Number(raw);
  if (!Number.isFinite(n)) return { ok: false, error: `${label} must be a number.` };
  if (n === 0) {
    return {
      ok: false,
      error: `${label} cannot be 0 — the engine reads 0 as “no limit”. Leave the field empty to remove the limit.`,
    };
  }
  if (n < 0) return { ok: false, error: `${label} must be greater than 0.` };
  if (integer && !Number.isInteger(n)) return { ok: false, error: `${label} must be a whole number.` };
  if (max != null && n > max) {
    return { ok: false, error: `${label} is a fraction of equity (0.35 = 35%), so it must be at most ${max}.` };
  }
  return { ok: true, value: n };
};

/**
 * Turn the form state into a PUT body.
 *
 * Only keys present in `values` are sent. A key whose value is an empty string
 * is sent as an explicit `null` — that is how the API is told to CLEAR a limit
 * rather than leave it untouched.
 *
 * @param {object} values  { dailyMaxLoss, maxOpenPositions, maxNotional,
 *   maxMarketBeta, maxFactorBeta, maxSectorWeight, blockExitsOnHalt }
 * @returns {{ok:true, body:object} | {ok:false, errors:string[]}}
 */
export const buildLimitsBody = (values = {}) => {
  const body = {};
  const errors = [];

  for (const field of ALL_LIMIT_FIELDS) {
    if (!(field.key in values)) continue;
    const r = normalizeLimit(values[field.key], { integer: field.integer, max: field.max, label: field.label });
    if (!r.ok) errors.push(r.error);
    else body[field.key] = r.value;
  }

  if ('blockExitsOnHalt' in values) body.blockExitsOnHalt = Boolean(values.blockExitsOnHalt);

  return errors.length ? { ok: false, errors } : { ok: true, body };
};

/** Everything the risk panel needs in one call. */
export const getRisk = () => http.get('/api/risk').then((res) => res.data || {});

/** @param {object} body from buildLimitsBody(). */
export const putLimits = (body) => http.put('/api/risk/limits', body).then((res) => res.data || {});

/**
 * Halt the caller's own trading.
 * `reason` is REQUIRED by the API (400 otherwise), max 512 chars.
 * `blockExits: true` traps open positions — the engine refuses exits too.
 */
export const halt = ({ reason, blockExits = false }) =>
  http.post('/api/risk/halt', { reason, blockExits: Boolean(blockExits) }).then((res) => res.data || {});

export const resume = () => http.post('/api/risk/resume', {}).then((res) => res.data || {});

/**
 * Close every open position.
 *
 * The API sets the halt flag FIRST unless `keepTrading` is true — otherwise the
 * strategy simply re-enters what the flatten just closed. That implicit halt
 * always carries `blockExits: false`. The work is ASYNCHRONOUS: progress
 * arrives per bot on `botUpdate:<botId>`; there is no completion event.
 */
export const flatten = ({ keepTrading = false, reason } = {}) =>
  http
    .post('/api/risk/flatten', { keepTrading: Boolean(keepTrading), ...(reason ? { reason } : {}) })
    .then((res) => res.data || {});

/** Admin only — stops EVERY user. 403 for anyone else. */
export const haltGlobal = ({ reason, blockExits = false }) =>
  http.post('/api/risk/halt/global', { reason, blockExits: Boolean(blockExits) }).then((res) => res.data || {});

export const resumeGlobal = () => http.post('/api/risk/resume/global', {}).then((res) => res.data || {});

const riskService = {
  getRisk,
  putLimits,
  halt,
  resume,
  flatten,
  haltGlobal,
  resumeGlobal,
  buildLimitsBody,
  normalizeLimit,
  LIMIT_FIELDS,
  EXPOSURE_FIELDS,
  ALL_LIMIT_FIELDS,
};

export default riskService;
